import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, switchMap, take, tap } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { CompositionNavContextService } from '../navigation/composition-nav-context.service';
import { PortfolioApiService } from '../../features/portfolio/services/portfolio-api.service';

const STORAGE_KEY = 'mg3p.portfolioId';

@Injectable({ providedIn: 'root' })
export class PortfolioContextService {
  private readonly auth = inject(AuthService);
  private readonly compositionNav = inject(CompositionNavContextService);
  private readonly portfolioApi = inject(PortfolioApiService);

  readonly portfolioIdAtivo = signal<string | null>(this.lerPersistido());

  definirPortfolioId(id: string): void {
    const trimmed = id.trim();
    if (!trimmed) {
      return;
    }
    localStorage.setItem(STORAGE_KEY, trimmed);
    this.portfolioIdAtivo.set(trimmed);
    this.compositionNav.setLastPortfolio(trimmed);
  }

  limparPortfolioId(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    this.portfolioIdAtivo.set(null);
  }

  resolverPortfolioId(): Observable<string | null> {
    const candidatos = [
      this.auth.portfolioId(),
      this.lerPersistido(),
      this.compositionNav.lastPortfolioId(),
    ]
      .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      .filter((id, index, arr) => arr.indexOf(id) === index);

    if (candidatos.length === 0) {
      return this.resolverAutomatico();
    }

    return this.validarCandidatos(candidatos, 0);
  }

  private validarCandidatos(candidatos: string[], indice: number): Observable<string | null> {
    if (indice >= candidatos.length) {
      this.limparPortfolioId();
      return this.resolverAutomatico();
    }

    const id = candidatos[indice];
    return this.portfolioApi.obter(id).pipe(
      take(1),
      map((portfolio) => {
        this.definirPortfolioId(portfolio.id);
        return portfolio.id;
      }),
      catchError(() => this.validarCandidatos(candidatos, indice + 1)),
    );
  }

  private resolverAutomatico(): Observable<string | null> {
    return this.portfolioApi.listar(0, 20).pipe(
      take(1),
      map((page) => {
        const ativo =
          (page.content ?? []).find((p) => p.status !== 'ARQUIVADO') ?? page.content?.[0];
        if (ativo?.id) {
          this.definirPortfolioId(ativo.id);
          return ativo.id;
        }
        return null;
      }),
      tap((id) => {
        if (!id) {
          this.limparPortfolioId();
        }
      }),
    );
  }

  private lerPersistido(): string | null {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v && v.trim().length > 0 ? v.trim() : null;
    } catch {
      return null;
    }
  }
}
